import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { useThemeStore } from '../../stores/theme.store';
import { useAuthStore } from '../../stores/auth.store';
import {
  Users, Wallet, Building2, Landmark, RefreshCw, Receipt, DollarSign,
  TrendingUp, ChevronLeft, ChevronRight, Calendar, AlertTriangle,
} from 'lucide-react';

function fmt(cents: number | string | null | undefined): string {
  const n = typeof cents === 'string' ? parseInt(cents, 10) : Number(cents);
  if (isNaN(n)) return 'R$ 0,00';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  return `${sign}R$ ${(abs / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function Row({ label, value, color, sub, bold }: { label: string; value: string; color?: string; sub?: string; bold?: boolean }) {
  const { isDarkMode } = useThemeStore();
  const ts = isDarkMode ? 'text-surface-400' : 'text-gray-500';
  const t = isDarkMode ? 'text-surface-100' : 'text-gray-900';
  return (
    <div className={`flex justify-between items-baseline py-1.5 ${bold ? 'border-t border-surface-700 pt-3 mt-1' : ''}`}>
      <span className={`text-sm ${ts}`}>{label}</span>
      <div className="text-right">
        <span className={`${bold ? 'text-lg font-bold' : 'font-semibold'} ${color || t}`}>{value}</span>
        {sub && <span className={`text-xs ${ts} ml-2`}>({sub})</span>}
      </div>
    </div>
  );
}

function getCurrentCycleMonth(cutoffDay: number): string {
  const now = new Date();
  const day = now.getDate();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  if (day >= cutoffDay) {
    const nextMonth = month + 1 > 12 ? 1 : month + 1;
    const nextYear = month + 1 > 12 ? year + 1 : year;
    return `${nextYear}-${String(nextMonth).padStart(2, '0')}`;
  }
  return `${year}-${String(month).padStart(2, '0')}`;
}

function prevMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const pm = m - 1 === 0 ? 12 : m - 1;
  const py = m - 1 === 0 ? y - 1 : y;
  return `${py}-${String(pm).padStart(2, '0')}`;
}

function nextMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const nm = m + 1 > 12 ? 1 : m + 1;
  const ny = m + 1 > 12 ? y + 1 : y;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

function formatMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${months[m - 1]}/${y}`;
}

export function FinancialSummaryPage() {
  const { isDarkMode } = useThemeStore();
  const authSuperAdmin = useAuthStore((s) => s.isSuperAdmin());
  const cutoffDay = 5;
  const currentMonth = getCurrentCycleMonth(cutoffDay);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  const { data: d, isLoading, refetch } = useQuery({
    queryKey: ['financial-summary', selectedMonth],
    queryFn: async () => (await api.get(`/admin/financial-summary?month=${selectedMonth}&cutoffDay=${cutoffDay}`)).data,
  });

  const t = isDarkMode ? 'text-surface-100' : 'text-gray-900';
  const ts = isDarkMode ? 'text-surface-400' : 'text-gray-500';
  const cb = isDarkMode ? 'bg-surface-800' : 'bg-gray-50';
  const bd = isDarkMode ? 'border-surface-700' : 'border-gray-200';
  const sec = `text-lg font-semibold ${t} mb-3 flex items-center gap-2`;

  const isSuperAdmin = (d?.isSuperAdmin === true) || authSuperAdmin;
  const isCurrentMonth = selectedMonth === currentMonth;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${t}`}>Relatório Financeiro Mensal</h1>
          <p className={ts}>Ciclo de cobrança: dia {cutoffDay} a dia {cutoffDay}</p>
        </div>
        <button onClick={() => refetch()} className={`p-2 rounded-lg ${cb} border ${bd}`}>
          <RefreshCw size={16} className={ts} />
        </button>
      </div>

      {/* Navegação de mês */}
      <Card className={`p-4 ${cb} flex items-center justify-between`}>
        <button onClick={() => setSelectedMonth(prevMonth(selectedMonth))} className={`flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-surface-700/50 ${ts} transition-colors`}>
          <ChevronLeft size={18} />
          <span className="text-sm">{formatMonth(prevMonth(selectedMonth))}</span>
        </button>
        <div className="text-center">
          <div className="flex items-center gap-2 justify-center">
            <Calendar size={18} className="text-primary-400" />
            <span className={`text-lg font-bold ${t}`}>{formatMonth(selectedMonth)}</span>
            {isCurrentMonth && <span className="text-xs bg-primary-500/20 text-primary-400 px-2 py-0.5 rounded-full">Atual</span>}
          </div>
          {d?.periodo && <p className={`text-xs ${ts} mt-1`}>Período: {d.periodo.label}</p>}
        </div>
        <button
          onClick={() => setSelectedMonth(nextMonth(selectedMonth))}
          disabled={selectedMonth >= currentMonth}
          className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors ${selectedMonth >= currentMonth ? 'opacity-30 cursor-not-allowed' : `hover:bg-surface-700/50 ${ts}`}`}
        >
          <span className="text-sm">{formatMonth(nextMonth(selectedMonth))}</span>
          <ChevronRight size={18} />
        </button>
      </Card>

      {!isCurrentMonth && (
        <button onClick={() => setSelectedMonth(currentMonth)} className="text-sm text-primary-400 hover:text-primary-300 flex items-center gap-1">
          <Calendar size={14} /> Voltar ao mês atual ({formatMonth(currentMonth)})
        </button>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className={`p-6 rounded-xl ${cb} animate-pulse`}><div className="h-8 w-32 bg-surface-700 rounded" /></div>)}
        </div>
      ) : d ? (
        <>
          {/* SALDO */}
          {(d.balancete?.saldoEmpresa || d.balancete?.saldoCorrentistas) && (
            <Card className={`p-6 ${cb} border-2 border-blue-500/30`}>
              <div className="flex items-center gap-3 mb-2">
                <Wallet size={24} className="text-blue-400" />
                <span className={`text-lg font-semibold ${t}`}>
                  {isSuperAdmin ? 'Saldo AnyPayBR (tempo real)' : 'Saldo consolidado correntistas'}
                </span>
              </div>
              <p className="text-4xl font-bold text-blue-400">
                {fmt(isSuperAdmin ? d.balancete.saldoEmpresa : d.balancete.saldoCorrentistas)}
              </p>
            </Card>
          )}

          {/* TAXAS SOBRE DEPÓSITOS */}
          <h2 className={sec}><DollarSign size={20} className="text-green-400" /> Taxas sobre Depósitos</h2>
          <Card className={`p-5 ${cb}`}>
            <Row label="Depósitos brutos no período" value={fmt(d.taxasDeposito?.depositosBrutos)} color="text-green-400" sub={`${d.taxasDeposito?.qtdDepositos} operações`} />
            <Row label="Taxa 5% total cobrada" value={fmt(d.taxasDeposito?.taxa5pctTotal)} bold />
            {isSuperAdmin && <Row label="→ 3% AnyPayBR (liquidante)" value={fmt(d.taxasDeposito?.taxa3pctAnypay)} color="text-orange-400" />}
            <Row label="→ 2% Invest Redenção" value={fmt(d.taxasDeposito?.taxa2pctInvest)} color="text-emerald-400" />
          </Card>

          {/* TAXAS DE SAQUE */}
          <h2 className={sec}><Receipt size={20} className="text-red-400" /> Taxas de Saque</h2>
          <Card className={`p-5 ${cb}`}>
            <Row label="Saques PIX no período" value={fmt(d.taxasSaque?.saquesBrutos)} color="text-red-400" sub={`${d.taxasSaque?.qtdSaques} operações`} />
            <Row label="Taxa R$1,20 cobrada" value={fmt(d.taxasSaque?.taxaSaqueTotal)} color="text-emerald-400" sub={`${d.taxasSaque?.qtdSaquesComTaxa} saques`} />
          </Card>

          {/* MENSALIDADES */}
          <h2 className={sec}><Users size={20} className="text-purple-400" /> Mensalidades R$21</h2>
          <Card className={`p-5 ${cb}`}>
            <h3 className={`font-semibold ${t} mb-3`}>Cobranças Realizadas</h3>
            <Row label="Pagou integral (saldo ≥ R$21)" value={fmt(d.mensalidades?.integral?.valor)} color="text-emerald-400" sub={`${d.mensalidades?.integral?.qtd} correntistas`} />
            <Row label="Pagou parcial (saldo > 0 e < R$21)" value={fmt(d.mensalidades?.parcial?.valorEfetivo)} color="text-amber-400" sub={`${d.mensalidades?.parcial?.qtd} correntistas`} />
            {Number(d.mensalidades?.estornos?.qtd) > 0 && (
              <Row label="Estornos" value={`-${fmt(d.mensalidades?.estornos?.valor)}`} color="text-red-400" sub={`${d.mensalidades?.estornos?.qtd}`} />
            )}
            <Row label="Total recebido" value={fmt(d.mensalidades?.totalRecebido)} color="text-emerald-400" bold />

            <h3 className={`font-semibold ${t} mb-3 mt-6`}>Pendências</h3>
            <Row label="Parcial (diferença R$21 - saldo)" value={fmt(d.mensalidades?.parcial?.pendencia)} color="text-amber-400" sub={`${d.mensalidades?.parcial?.qtd} correntistas`} />
            <Row label="Sem saldo (R$21 inteiro pendente)" value={fmt(d.mensalidades?.semSaldo?.pendencia)} color="text-red-400" sub={`${d.mensalidades?.semSaldo?.qtdCorrentistas} correntistas sem saldo`} />
            <Row label="Não cobradas (contas novas)" value={fmt(d.mensalidades?.naoCobradas?.pendencia)} color="text-surface-400" sub={`${d.mensalidades?.naoCobradas?.qtdContas} contas`} />
            {(d.mensalidades?.naoCobradas?.comSaldoAcima21 > 0 || d.mensalidades?.naoCobradas?.comSaldoParcial > 0 || d.mensalidades?.naoCobradas?.semSaldo > 0) && (
              <div className="ml-4 mt-1 space-y-1.5">
                {d.mensalidades?.naoCobradas?.comSaldoAcima21 > 0 && (
                  <p className={`text-xs text-emerald-400`}>→ {d.mensalidades.naoCobradas.comSaldoAcima21} com saldo ≥ R$21 — pode descontar {fmt(d.mensalidades.naoCobradas.comSaldoAcima21Valor)}</p>
                )}
                {d.mensalidades?.naoCobradas?.comSaldoParcial > 0 && (
                  <p className={`text-xs text-amber-400`}>→ {d.mensalidades.naoCobradas.comSaldoParcial} com saldo {'>'} 0 e {'<'} R$21 — disponível {fmt(d.mensalidades.naoCobradas.comSaldoParcialDisponivel)}, pendente {fmt(d.mensalidades.naoCobradas.comSaldoParcialPendente)}</p>
                )}
                {d.mensalidades?.naoCobradas?.semSaldo > 0 && (
                  <p className={`text-xs text-red-400`}>→ {d.mensalidades.naoCobradas.semSaldo} sem saldo — pendente {fmt(d.mensalidades.naoCobradas.semSaldoPendencia)}</p>
                )}
                <div className={`mt-2 pt-2 border-t border-surface-700 flex justify-between`}>
                  <span className={`text-xs font-semibold text-emerald-400`}>Pode descontar agora: {fmt(d.mensalidades.naoCobradas.podeDescontarAgora)}</span>
                  <span className={`text-xs font-semibold text-red-400`}>Fica pendente: {fmt(d.mensalidades.naoCobradas.ficaPendente)}</span>
                </div>
              </div>
            )}
            <Row label="Total pendências" value={fmt(d.mensalidades?.totalPendencia)} color="text-red-400" bold />
          </Card>

          {/* RECEITA INVEST REDENÇÃO */}
          <h2 className={sec}><Landmark size={20} className="text-emerald-400" /> Receita Invest Redenção</h2>
          <Card className={`p-5 ${cb} border-2 border-emerald-500/30`}>
            <h3 className={`font-semibold ${t} mb-3`}>Recebido no Período</h3>
            <Row label="Taxa 2% sobre depósitos" value={fmt(d.receitaInvest?.taxa2pct)} color="text-emerald-400" />
            <Row label="Mensalidades recebidas" value={fmt(d.receitaInvest?.mensalidadesRecebidas)} color="text-emerald-400" />
            {Number(d.receitaInvest?.taxasReconciliacao || 0) > 0 && (
              <Row label="Taxas reconciliação" value={fmt(d.receitaInvest?.taxasReconciliacao)} color="text-emerald-400" />
            )}
            <Row label="TOTAL RECEBIDO" value={fmt(d.receitaInvest?.totalRecebido)} color="text-emerald-400" bold />

            <h3 className={`font-semibold ${t} mb-3 mt-6 flex items-center gap-2`}>
              <AlertTriangle size={16} className="text-amber-400" /> Pendências
            </h3>
            <Row label="Total pendências mensalidade" value={fmt(d.receitaInvest?.totalPendencias)} color="text-amber-400" />
            <p className={`text-xs ${ts} mt-2`}>
              Pendências = mensalidades de correntistas sem saldo. Quando depositarem, o saldo negativo absorve automaticamente.
            </p>
          </Card>

          {/* CUSTOS ANYPAY BR — Super Admin */}
          {isSuperAdmin && d.custoLiquidante && (
            <>
              <h2 className={sec}><Building2 size={20} className="text-orange-400" /> Custos AnyPayBR (Liquidante)</h2>
              <Card className={`p-5 ${cb} border border-orange-500/30`}>
                <Row label="Taxa 3% sobre depósitos" value={fmt(d.custoLiquidante?.taxa3pct)} color="text-orange-400" />
                <Row label="Custo R$1,20 por saque" value={fmt(d.custoLiquidante?.custoSaques)} color="text-orange-400" sub={`${d.custoLiquidante?.qtdSaques} saques`} />
                <Row label="TOTAL CUSTO LIQUIDANTE" value={fmt(d.custoLiquidante?.total)} color="text-orange-400" bold />
              </Card>
            </>
          )}

          {/* CORRENTISTAS (snapshot) */}
          <h2 className={sec}><Users size={20} className="text-cyan-400" /> Correntistas (situação atual)</h2>
          <Card className={`p-5 ${cb}`}>
            <Row label="Com saldo positivo" value={fmt(d.saldos?.correntistas?.saldoPositivo)} color="text-green-400" sub={`${d.saldos?.correntistas?.comSaldo} contas`} />
            <Row label="Saldo negativo" value={fmt(d.saldos?.correntistas?.saldoNegativo)} color="text-red-400" sub={`${d.saldos?.correntistas?.negativos} correntistas`} />
            <Row label="Saldo zero" value={String(d.saldos?.correntistas?.semSaldo || 0)} sub="contas" />
            <p className={`text-xs ${ts} mt-2`}>Total ativos: {d.saldos?.correntistas?.totalAtivos}</p>
          </Card>

          {/* ACUMULADO */}
          {d.acumulado && (
            <>
              <h2 className={`${sec} mt-4`}><TrendingUp size={20} className="text-blue-400" /> Acumulado (desde o início)</h2>
              <Card className={`p-5 ${cb}`}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className={`text-xs ${ts}`}>Total depositado</p>
                    <p className="font-bold text-green-400">{fmt(d.acumulado.depositosBrutos)}</p>
                    <p className={`text-xs ${ts}`}>{d.acumulado.qtdDepositos} depósitos</p>
                  </div>
                  <div>
                    <p className={`text-xs ${ts}`}>Total sacado</p>
                    <p className="font-bold text-red-400">{fmt(d.acumulado.saquesPix)}</p>
                    <p className={`text-xs ${ts}`}>{d.acumulado.qtdSaques} saques</p>
                  </div>
                </div>
              </Card>
            </>
          )}

          <p className={`text-xs ${ts} text-center mt-4`}>
            Gerado em {new Date(d.generatedAt).toLocaleString('pt-BR')} • Período: {d.periodo?.label}
          </p>
        </>
      ) : null}
    </div>
  );
}

export default FinancialSummaryPage;
